import type { EChartsOption } from 'echarts'
import type { IWidget } from '@/interfaces/components/dashboard/widget'
import { useCompanyDashboardStore } from '@/store/companyDashboard'
import { resolveWidgetUnit } from '@/utils/sensor-schema-fields'
import {
  formatWidgetMetricValue,
  isDurationMinutesField,
  resolveWidgetDisplayUnit,
} from '@/utils/dashboard/format-widget-metric'
import { storeToRefs } from 'pinia'

export const GAUGE_ACCENT_COLOR = '#123ADD'
export const GAUGE_ALERT_COLOR = '#FB5454'

const COLOR_ALERT = GAUGE_ALERT_COLOR
const COLOR_NORMAL = GAUGE_ACCENT_COLOR
const COLOR_TRACK = '#E2E8F0'

const GAUGE_CENTER: [string, string] = ['50%', '68%']
const GAUGE_RADIUS = '105%'
const BASE_ARC_WIDTH = 14

function clampScale(scale: number): number {
  return Math.min(1, Math.max(0.45, scale))
}

export function computeGaugeScale(width: number, height: number): number {
  if (width <= 0 || height <= 0) return 1
  return clampScale(Math.min(width, height * 1.4) / 160)
}

export function useGaugeWidget() {
  const store = useCompanyDashboardStore()
  const { widgetData } = storeToRefs(store)

  const getValue = (widget: IWidget): string => {
    const state = widgetData.value[widget.id]
    if (!state || state.loading) return '...'
    if (state.error || state.value === null) return '--'

    const dataField = widget.config.data_field
    if (isDurationMinutesField(dataField)) {
      return formatWidgetMetricValue(state.value, dataField ?? '')
    }

    return state.value.toFixed(1)
  }

  const getUnit = (widget: IWidget): string => {
    const state = widgetData.value[widget.id]
    const unit = resolveWidgetUnit(widget, state?.unit ?? '')
    return resolveWidgetDisplayUnit(widget.config.data_field, unit)
  }

  const isLoading = (widget: IWidget): boolean => {
    return widgetData.value[widget.id]?.loading ?? false
  }

  const hasError = (widget: IWidget): boolean => {
    return widgetData.value[widget.id]?.error ?? false
  }

  const getNumericValue = (widget: IWidget): number => {
    const state = widgetData.value[widget.id]
    if (!state || state.loading || state.error || state.value === null) return 0
    return state.value
  }

  const isInAlertState = (widget: IWidget): boolean => {
    const state = widgetData.value[widget.id]
    if (!state || state.loading || state.error || state.value === null) return false

    const threshold = widget.config.threshold ?? 70
    const operator = widget.config.threshold_operator || 'greater'

    return operator === 'greater' ? state.value > threshold : state.value < threshold
  }

  const getAccentColor = (widget: IWidget): string =>
    isInAlertState(widget) ? COLOR_ALERT : COLOR_NORMAL

  const getOption = (widget: IWidget, scale = 1): EChartsOption => {
    const scaled = clampScale(scale)
    const arcWidth = Math.max(5, Math.round(BASE_ARC_WIDTH * scaled))
    const valueFontSize = Math.round(32 * scaled)
    const unitFontSize = Math.round(14 * scaled)
    const axisLabelFontSize = Math.round(12 * scaled)
    const axisLabelDistance = Math.round(-48 * scaled)
    const pointerWidth = Math.max(2, Math.round(3 * scaled))

    const state = widgetData.value[widget.id]
    const dataField = widget.config.data_field ?? ''
    const unit = resolveWidgetDisplayUnit(
      dataField,
      resolveWidgetUnit(widget, state?.unit ?? ''),
    )
    const min = widget.config.min ?? 0
    const max = widget.config.max ?? 100
    const threshold = widget.config.threshold ?? 70

    let lastValue = min
    if (!state || state.loading) {
      lastValue = min
    } else if (state.error || state.value === null) {
      lastValue = min
    } else {
      lastValue = state.value
    }

    const isAlert = isInAlertState(widget)
    const mainColor = isAlert ? COLOR_ALERT : COLOR_NORMAL
    const detailColor = !state || state.loading || state.error ? '#464646' : '#000000'

    const displayValue = !state || state.loading
      ? '...'
      : state.error || state.value === null
        ? '--'
        : isDurationMinutesField(dataField)
          ? formatWidgetMetricValue(lastValue, dataField)
          : lastValue.toFixed(1)

    const valueUnitText = unit
      ? `{value|${displayValue}}{unit| ${unit}}`
      : `{value|${displayValue}}`

    return {
      series: [
        {
          type: 'gauge',
          startAngle: 180,
          endAngle: 0,
          min,
          max,
          splitNumber: 1,
          radius: GAUGE_RADIUS,
          center: GAUGE_CENTER,
          axisLine: {
            lineStyle: { width: arcWidth, color: [[1, COLOR_TRACK]] },
          },
          progress: {
            show: true,
            width: arcWidth,
            itemStyle: { color: mainColor },
          },
          pointer: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: {
            color: '#64748B',
            fontSize: axisLabelFontSize,
            distance: axisLabelDistance,
            formatter: (value: number) =>
              Math.abs(value - min) < 0.01 || Math.abs(value - max) < 0.01 ? String(value) : '',
          },
          title: { show: false },
          detail: { show: false },
          data: [{ value: lastValue }],
        },
        {
          type: 'gauge',
          startAngle: 180,
          endAngle: 0,
          min,
          max,
          radius: GAUGE_RADIUS,
          center: GAUGE_CENTER,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          progress: { show: false },
          detail: { show: false },
          title: { show: false },
          pointer: {
            icon: 'rect',
            length: '12%',
            width: pointerWidth,
            offsetCenter: [0, '-88%'],
            itemStyle: { color: COLOR_ALERT },
          },
          data: [{ value: threshold }],
          z: 10,
        },
      ],
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: '56%',
          style: {
            text: valueUnitText,
            rich: {
              value: {
                fill: detailColor,
                font: `${valueFontSize}px Satoshi, sans-serif`,
              },
              unit: {
                fill: '#64748B',
                font: `${unitFontSize}px Satoshi, sans-serif`,
                padding: [Math.round(4 * scaled), 0, 0, 2],
              },
            },
          },
        },
      ],
    }
  }

  return {
    getOption,
    getValue,
    getUnit,
    isLoading,
    hasError,
    getNumericValue,
    isInAlertState,
    getAccentColor,
    ACCENT_COLOR: GAUGE_ACCENT_COLOR,
    ALERT_COLOR: GAUGE_ALERT_COLOR,
  }
}
